import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { ActionQueueItem } from '../../utils/operationsDashboardUtils';
import { getPriorityColor } from '../../constants/operationsDashboardColors';
import { DashboardDensity, isCompact } from '../../utils/dashboardDensity';

interface SupervisorActionQueueProps {
  items: ActionQueueItem[];
  onAction: (item: ActionQueueItem) => void;
  density?: DashboardDensity;
  embedded?: boolean;
}

const SupervisorActionQueue: React.FC<SupervisorActionQueueProps> = ({
  items,
  onAction,
  density = 'standard',
  embedded = false,
}) => {
  const compact = isCompact(density);

  const content = (
    <>
      {!embedded && (
        <Typography variant="h6" fontWeight={700} gutterBottom>
          Supervisor Action Queue ({items.length})
        </Typography>
      )}
      {items.length === 0 ? (
        <Typography variant="caption" color="text.secondary">No pending actions.</Typography>
      ) : (
        <Stack spacing={compact ? 0.5 : 1}>
          {items.map((item) => (
            <Box
              key={item.id}
              sx={{
                p: compact ? 0.75 : 1,
                borderRadius: 0.5,
                border: '1px solid',
                borderColor: 'divider',
                borderLeft: `3px solid ${getPriorityColor(item.priority)}`,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={0.5}>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Chip
                    size="small"
                    label={item.priority.charAt(0).toUpperCase()}
                    sx={{
                      bgcolor: getPriorityColor(item.priority),
                      color: '#fff',
                      height: 16,
                      fontSize: '0.6rem',
                      mb: 0.25,
                      '& .MuiChip-label': { px: 0.5 },
                    }}
                  />
                  <Typography variant="caption" fontWeight={600} display="block" sx={{ fontSize: compact ? '0.7rem' : '0.75rem', lineHeight: 1.3 }}>
                    {item.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ fontSize: '0.65rem' }}>
                    {item.campaignName}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  variant="text"
                  sx={{ minWidth: 0, px: 0.5, fontSize: '0.65rem' }}
                  onClick={() => onAction(item)}
                >
                  Open
                </Button>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </>
  );

  if (embedded) return <Box>{content}</Box>;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ p: compact ? 1.5 : 2, '&:last-child': { pb: compact ? 1.5 : 2 } }}>
        {content}
      </CardContent>
    </Card>
  );
};

export default SupervisorActionQueue;
